import Link from "@mui/material/Link";

import { UserAnalyticsService } from "@analytics/index";

import UserApi from "@api/user.api";

import ContactSalesButton from "@components/ContactSalesButton";
import PrivacyPolicyAndTermsOfServiceModal from "@components/Popups/PrivacyPolicyAndTermsOfServiceModal";
import ProductUpdatesModal from "@components/Popups/ProductUpdatesPopup";

import C from "@constants/constants";

import { IUser } from "@interfaces/user.type";

import { PopupActions } from "@store/popupStore";

export const openContactSalesNotification = () =>
  PopupActions.openNotification({
    content: (
      <>
        Please contact our sales team and we’ll get you started quickly.
        <br />
        <br />
        <ContactSalesButton />
      </>
    ),
    title: "Thanks for your interest in ScaleIP!",
  });

export const openProductUpdateNotification = (user: IUser) =>
  PopupActions.openNotification({
    content: <ProductUpdatesModal />,
    onExit: () => {
      void (async () => {
        try {
          await UserApi.update(user.id, { hasAcknowledgedProductUpdate: true });
          UserAnalyticsService.userAcknowledgedProductUpdate(user.id || "", user.firstName || "", user.lastName || "");
        } catch {
          console.error("User acknowledgment failed");
        }
      })();
    },
    title: "AI-Generated Marketing Summaries",
  });

export const openPrivacyPolicyAndTermsOfServiceConfirmation = (
  user: IUser,
  checkShowProductUpdateModal: () => boolean,
) =>
  PopupActions.openBlockingModal({
    content: (
      <PrivacyPolicyAndTermsOfServiceModal
        isPrivacyPolicyAccepted={user.isPrivacyPolicyAccepted || false}
        isTermsOfServiceAccepted={user.isTermsOfServiceAccepted || false}
        onSubmit={() => {
          void UserApi.update(user.id, { isPrivacyPolicyAccepted: true, isTermsOfServiceAccepted: true })
            .then(() => {
              PopupActions.closeBlockingModal();
              void checkShowProductUpdateModal();
            })
            .catch(() => {
              // Handle error appropriately
              PopupActions.closeBlockingModal();
            });
        }}
      />
    ),
    title: "Updated privacy policy and terms of service.",
  });

export const openAccountExistsNotification = () =>
  PopupActions.openNotification({
    content: (
      <>
        An account with this email has already been registered.
        <br />
        Please <Link href={`${C.VITE_APP_APP}/login`}>Login</Link>.
      </>
    ),
    title: "Email already exists",
  });

export const openAccountDoesNotExistNotification = () =>
  PopupActions.openNotification({
    content: (
      <>
        However, we have not found an account associated with your email. Please{" "}
        <Link href="https://app.scaleip.com/signup">Sign up</Link>.
      </>
    ),
    title: "Thanks for your interest in ScaleIP!",
  });

export const openInvalidCredentialsNotification = () =>
  PopupActions.openNotification({
    content: <>Please verify your email and password to log in.</>,
    title: "Invalid Credentials",
  });

export const openEmailNotVerifiedNotification = () =>
  PopupActions.openNotification({
    content: <>Please verify your email to log in.</>,
    title: "Email not verified",
  });

export const openPasswordResetEmailSuccessNotification = () =>
  PopupActions.openNotification({
    content: "If your email is registered with an account, we will send you an email to reset your password.",
    title: "Thank You!",
  });

export const openPasswordResetEmailFailedNotification = () =>
  PopupActions.openNotification({
    content: "There was an error sending an email, please try again.",
    title: "Error",
  });

export const openPasswordResetSuccessNotification = () =>
  PopupActions.openNotification({
    content: "Your password has been reset.",
    title: "Thank You!",
  });

export const openPasswordResetFailedNotification = () =>
  PopupActions.openNotification({
    content: "There was an error resetting your password. Please try again.",
    redirect: `${C.VITE_APP_APP}/signin`,
    title: "Error",
  });

export const openUserUpdateSuccessAlert = () =>
  PopupActions.openAlert({
    duration: 2000,
    message: "Account details updated.",
    severity: "success",
  });

export const openUserUpdateFailedNotification = () =>
  PopupActions.openNotification({ content: "Something's wrong. Please try again later", title: "Error" });
